import {CF} from "module/CustomField/util/CustomField";
import _ from 'lodash';
import $ from 'jquery';
import Backbone from 'backbone';
import ErrMsgDict from "string/ErrorMessageStrings";
import {getter, setter} from "util/BackboneCompatUtil";
import {getDqParamsObject} from 'module/CustomField/util/DqParam';
import {CFMember, CFValue} from 'module/CustomField/type/CFType'
import {searchDq} from "module/Dq/searchDq";
import {isDqSearchable} from 'module/Dq/util/DqParser'

export const Dq = Backbone.View.extend({
	/**
	 * @param options
	 * @param {String} options.url url for datasource
	 * @param [options.cfDef] use data from cfdef: dqkey, fieldName, parameters
	 * @param [options.pos] position of dq cf.-namespace field
	 * @param options.dqkey AacfRequestTO id
	 * @param {{} | function} options.parameters additional request parameters in format { dstName: srcName } or a function that returns the already prepared {key:value} object to append to the request
	 * @param {boolean} [options.includeSearchTerm = true] true: include typed values into ajax request, false: only use url
	 * @param {*} [options.nullValueRepresentation = null]
	 * @param {function} [options.getData] function that returns a $.Deferred, that is resolved with the available values. This can be used if the data is already present and no server call is necessary.
	 * @param options.fieldName key-name of the model attribute
	 * @param {String}[options.dataId = 'value'] key-name of the value in a result object when using internal getData
	 * @param {String}[options.dataText = 'label'] key-name of the label in a result object when using internal getData
	 */
	initialize: function (options: any) {
		this.url = options.url || '../json/aacf';
		this.cfDef = options.cfDef;
		if (this.cfDef) {
			this.dqkey = this.cfDef.dqKey;
			// this.nullValueRepresentation = this.cfDef.nullValue; //done in cfDef setter
			this.pos = CF.isCFNamespace(this.cfDef) ? options.pos : undefined
			this.includeSearchTerm = true;
			this.isAze = options.isAze || false;
		} else {
			this.dqkey = options.dqkey;
			this.fieldName = options.fieldName;
			this.parameters = options.parameters;
			this.includeSearchTerm = options.includeSearchTerm !== false;
			this.nullValueRepresentation = options.nullValueRepresentation !== undefined ? options.nullValueRepresentation : null;
		}
		if (typeof options.getData === 'function') this.getData = options.getData;
		this.dataId = options.dataId || 'value';
		this.dataText = options.dataText || 'label';
	},

	// model setter & getter

	/**
	 * @param {{}} [options]getViewValueFromModel
	 * @param {boolean} [options.forView = false]
	 * @returns {*}
	 */
	getValueFromModel: function (options?: { forView?: boolean }) {
		let val;
		if (this.cfDef) {
			val = CF.getValueFrom(this.cfDef, this.model, {pos: this.pos, ...options});
		} else {
			val = getter(this.model, this.fieldName);
			if (this.nullValueRepresentation === val) val = null;
		}
		return val;
	},
	setValueToModel: function (value: CFValue | CFMember) {
		if (this.cfDef) {
			CF.setValueTo(this.cfDef, this.model, value, {pos: this.pos});
		} else {
			if (value === null) {
				value = this.nullValueRepresentation;
			}
			setter(this.model, this.fieldName, value);
		}
	},

	// SERVER CALLS


	/**
	 * @param [options]
	 * @param [options.searchTerm]
	 * @returns {$.Deferred | []}
	 */
	search: function (options?: { searchTerm?: string }) {
		let params = this.getCurrentRequestParams();
		let filter = this.includeSearchTerm && options && options.searchTerm || '';
		const dqKey = this.dqkey;
		const searchable = isDqSearchable(dqKey)
		if (!searchable) {
			filter = '';
		}
		let jsonData = {
			id: dqKey,
			filter: filter,
			params: params,
			isAze: this.isAze,
		};
		return Promise.resolve(this.getData(jsonData)) // Promise.resolve for external getData functions and always return Promise
	},
	searchCurrentDqEntry() {
		let val = this.getValueFromModel();
		return this.search({searchTerm: val}).then((data: any) => _.find(data, d => d.value == val));
	},
	/**
	 *
	 * @param jsonData
	 * @returns {*} deferred resolved with array or array with following keys:
	 *                value: the value to set on cfDef or fieldName
	 *                label: the label. can be searched, visible for user when there is no...
	 *                htmlLabel: same as label but with html enriched. this must contain the same information as label
	 *                    (htmlLabel is separated so the html text does not interfere the user when searching, i.e. icon and class names)
	 */
	getData: function (jsonData: {
		id: string,
		filter: string,
		params: { [key in string]: string | number },
		isAze?: boolean
	}) {
		return searchDq({
			dqkey: jsonData.id,
			params: jsonData.params,
			filter: jsonData.filter,
			isAze: jsonData.isAze,
			url: this.url,
			dataId: this.dataId,
			dataText: this.dataText,
		})
	},
	getCurrentRequestParams: function () {
		let that = this;
		let params: Record<string, string | number | null | undefined> = {};
		if (this.cfDef) {
			params = getDqParamsObject(this.cfDef.dqParams, this.model, this.pos);
		} else { // NOT cfDef, static usage
			if (typeof this.parameters === "function") {
				params = this.parameters() || {};
			} else {
				_.each(this.parameters, function (field, p) {
					if (getter(that.model, field)) {
						params[p] = getter(that.model, field);
					}
				});
			}
		}
		return params;
	},
	filterDataBySearchTerm: function (data: any[], options?: { searchTerm?: string }) {
		let term = options && options.searchTerm;
		// do not search without text
		if (!term || !((term += "").trim())) return data;
		// escape regex chars. allows special character search e.g. C++
		let escaped = term.replace(/[\-\[\]{}()+?.,\\\^$|#\s]/g, "\\$&");
		// but allow * as wildcard
		escaped = escaped.replace(/\*/g, ".*");
		// searchterm can be any part of value+label
		escaped = '.*' + escaped + '.*';
		let regExp = new RegExp(escaped, 'i');
		let exactMatch: any
		let filtered = _.filter(data, function (d) {
			if (d.value === term && !exactMatch) {
				exactMatch = d;
				return false;
			}
			return regExp.test(d.value + '' + d.label);
		});
		if (exactMatch) filtered.unshift(exactMatch);
		return filtered;
	},


	// VALIDATE
	/**
	 * requires a correctly filled fieldModel and setValue(value) before performing
	 * @param currentValue
	 * @param rules {{}} if not set, false is returned (no error)
	 * @param [rules.required] {boolean}
	 * @param [rules.mustMatch] {boolean}
	 * @param [rules.forceDq] {boolean} overrides required and mustMatch. When dq contains ' ' then required=false. For required/mustMatch configs that are dependent on other parameters
	 * @returns {boolean | $.Deferred} false when no error, or errorobject in format {field: 'String', message: 'String'}, or deferred, rejected with error
	 */
	getErrorByRules: function (currentValue: any, rules?: { required: boolean, mustMatch: boolean, forceDq: boolean }) {
		if (!rules) return false;
		let $_required = $.Deferred();
		let $_mustMatch = $.Deferred();
		let $_result = $.when($_required, $_mustMatch);
		if (this.nullValueRepresentation === currentValue) currentValue = null;
		const errorKey = {
			field: this.cfDef && this.cfDef.id || this.fieldName,
			pos: this.pos,
		}
		//$_1
		if (rules.forceDq || !rules.required) {
			$_required.resolve();
		} else {
			if (!currentValue || !_.trim(currentValue)) {
				$_required.reject({...errorKey, message: ErrMsgDict.missingValue});
			} else {
				$_required.resolve();
			}
		}
		//$_2
		if (!rules.forceDq && (!currentValue || currentValue === ' ' || !rules.mustMatch)) {
			$_mustMatch.resolve();
		} else {
			let deferdata = this.search({searchTerm: currentValue}); // must have been loaded before
			deferdata.then((data: any) => {
				let testVal = currentValue;
				if (typeof currentValue === 'string') testVal = testVal.trim();
				let fieldObject = _.find(data, (d) => {
					if(d.disabled) return false; // disabled values are not allowed in mustmatch validation rule
					//allow typeconversions (when model.id is number and model value is string)
					//when datasource contains ' ', no value is also valid, even when required=false
					return (rules.forceDq && d.value === ' ' && !testVal) || d.value == testVal;
				});
				if (rules.forceDq && !testVal && !fieldObject) {
					$_mustMatch.reject({...errorKey, message: ErrMsgDict.missingValue});
				} else if (!fieldObject) {
					$_mustMatch.reject({...errorKey, message: ErrMsgDict.notAvailableValue});
				} else {
					$_mustMatch.resolve();
				}
			}).catch(() => {
				$_mustMatch.reject({...errorKey, message: ErrMsgDict.errorTransmission});
			});
			//$_2 END
		}
		return $_result;
	},


	// Helper
	getValueChangeEvent: function () {
		if (this.cfDef) {
			return toChangeEvents(this.cfDef.dstField);
		} else {
			return toChangeEvents(this.fieldName);
		}
	},
	getRelevantChangeEvents: function () {
		let fieldNames;
		if (this.cfDef) {
			fieldNames = CF.getDqRelevantFieldNames(this.cfDef);
		} else {
			let paramNames;
			if (typeof this.parameters === 'function') paramNames = _.keysIn(this.parameters()); // do not rename parameters
			else if (this.parameters) paramNames = _.values(this.parameters);
			fieldNames = [this.fieldName].concat(paramNames);
		}
		fieldNames = _.uniq(fieldNames);
		return toChangeEvents(fieldNames)
	},
});

function toChangeEvents(fieldNames: string[] | string) {
	return _.map(([] as string[]).concat(fieldNames), fn => "change:" + fn).join(' ');
}